import {
    EonUiIcon,
    EonUiInput,
    EonUiPreloaderSquares,
    EonUiTagPill,
    EonUiTagPillGroup,
} from "@eon-ui/eon-ui-components-react";
import { ExpandingInput } from '../../shared/ui/molecules/ExpandingInput';
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { raiseIncident } from "../../services/raiseIncident";
import { AiAssistContext } from "../ai-assist-refactored/AiAssist.context";
import { FeedbackGetterProps, FeedbackSetterProps } from "./feedback.component";
import { ThemedIcon } from "@me8eon/icons";
import { useFeedback, extractSourceFromAnswer, extractNewAiSource } from "@me8eon/feedback";
import { useUserData } from "@me8eon/react_authentication";
import { useMessages } from "@me8eon/sessions_messages";
import { useTranslation } from "@me8eon/translation";

interface IFeedbackCreateIncidentComponentProps {
    setters: FeedbackSetterProps;
    getters: FeedbackGetterProps;
}

export const CreateIncidentInput = ({
    isEditIncident,
    disabled,
    setIncTextFunc,
    setContactFunc,
    incidentInvalid,
    contactInvalid,
    incidentText,
    isIncidentTextPending,
    setIncidentText
}: { isEditIncident: boolean, disabled?: boolean, setIncTextFunc?: (val: string) => void, setContactFunc?: (val: string) => void, incidentInvalid?: boolean, contactInvalid?: boolean, incidentText?: string, isIncidentTextPending?: boolean, setIncidentText?: (val: string) => void }) => {
    const t = useTranslation();
    const { contactNumber, setContact } = useContext(AiAssistContext);
    const handleSetIncidentText = useCallback(
        (text: string) => {
            setIncidentText && setIncidentText(text);
        },
        [setIncidentText]
    );
    const setContactNumber = useCallback(
        (text: string) => {
            setContact(text);
        },
        [setContact]
    );

    useEffect(() => {
        setContactFunc && setContactFunc(contactNumber);
    }, [contactNumber, setContactFunc]);

    return (
        <>
            {isIncidentTextPending ? (
                <EonUiPreloaderSquares
                    className="preloader"
                />
            ) : isEditIncident ? (
                <div>
                    <ExpandingInput
                        value={incidentText || ""}
                        style={{ marginTop: "12px" }}
                        invalid={incidentInvalid}
                        errorMessage={t("chatBot.incError")}
                        onValueChanged={(e) => {
                            const value = e.target.value;
                            handleSetIncidentText(value);
                            setIncTextFunc && setIncTextFunc(value);
                        }}
                        disabled={disabled ?? false}
                    />
                    {setContactFunc && (<div style={{ background: "#fafafa", borderRadius: "8px", padding: "18px", marginTop: "12px" }}>
                        <label htmlFor="contact_field" style={{ fontSize: "16px", fontWeight: 500 }}>{t("chatBot.contactNumberLabel")}</label>
                        <EonUiInput
                            value={contactNumber}
                            size="normal"
                            id="contact_field"
                            style={{ maxWidth: "200px", marginTop: "8px" }}
                            placeholder={"+491234567890"}
                            invalid={contactInvalid}
                            errorMessage={t("chatBot.contactError")}
                            onValueChanged={(e) => {
                                const value = e.target.value;
                                setContactNumber(value);
                                setContactFunc && setContactFunc(value);
                            }}
                            disabled={disabled ?? false}
                        />
                    </div>)}
                </div>
            ) : (
                <div>{incidentText}</div>
            )}
        </>
    );
};

export const CreateIncidentResponse = ({
    incidentResponse,
    confirmTicketCreation,
    respLoading
}: { incidentResponse: any, confirmTicketCreation: boolean, respLoading: boolean }) => {

    const t = useTranslation();
    const feedbackFn = useFeedback();
    const prevIncidentNumber = useRef<string | undefined>(undefined);
    const { incidentText, actualMessages, currentAgent } = useContext(AiAssistContext);
    const [messages] = useMessages();
    const userData = useUserData();
    const { company, featureFlagEnabled, currentRole } = userData;

    const getSecondLastObj = (val: string) => {
        const filtered = messages.filter(m => m.role === val);
        return filtered.length > 1
            ? (filtered[filtered.length - 2].content as string)
            : "";
    };

    useEffect(() => {
        const currentNumber = incidentResponse?.number;

        // only send feedback if a new incident number appears (and it's not same as previous)
        if (currentNumber && currentNumber !== prevIncidentNumber.current) {
            // Get the last user query and bot answer from messages
            const lastUserMessage = actualMessages.filter(msg => msg.role === "user").slice(-1)[0];
            const lastBotMessage = actualMessages.filter(msg => msg.role === "assistant").slice(-1)[0];

            const query = lastUserMessage?.content || "";
            const answer = lastBotMessage?.content || "";

            const userQuery = featureFlagEnabled ? getSecondLastObj("user") : query as string;
            const userAnswer = featureFlagEnabled ? getSecondLastObj("assistant") : answer as string;
            const ansSource = featureFlagEnabled ? extractNewAiSource(userAnswer) : extractSourceFromAnswer(answer as string);
            feedbackFn({
                source: "incident",
                classification: undefined,
                incNumber: currentNumber,
                text: incidentText || "",
                query: userQuery,
                answer: userAnswer,
                sourceFromAnswer: ansSource,
                company: company || "unknown",
                role: currentRole || "unknown",
                short_description: incidentResponse?.short_description || ""
            });
            // update previous number
            prevIncidentNumber.current = currentNumber;
        }
    }, [incidentResponse?.number, feedbackFn, incidentText, actualMessages, userData]);

    return (
        <>
            {respLoading ? (
                <div className="loading">
                    <EonUiPreloaderSquares
                        className="preloader"
                    />
                </div>
            ) : confirmTicketCreation ? (
                incidentResponse?.number ? (
                    <div className="ticket-success">
                        <EonUiIcon
                            className="user-icon eon-ui-svg-icon"
                            name="radio_checkbox_tick"
                            scheme="red500"
                        />
                        <p>
                            {t("chatBot.ticketSuccessMsg")}
                            <a target="_blank" href="/incidents" className="link">{incidentResponse.number}</a>
                        </p>
                    </div>
                ) : (
                    <div className="failure-msg">
                        <EonUiIcon
                            className="user-icon eon-ui-svg-icon"
                            name="error"
                            scheme="red500"
                        />
                        <p>
                            {t("chatBot.ticketErrorMsg")}
                        </p>
                    </div>
                )
            ) : null}
        </>
    );
};

const FeedbackCreateIncidentComponent = ({
    setters,
    getters
}: IFeedbackCreateIncidentComponentProps) => {
    const t = useTranslation();
    const [isEditIncident, setIsEditIncident] = useState(false);
    const { incidentText, contactNumber, incidentFiles, setIncidentFiles } = useContext(AiAssistContext);
    const [incidentResponse, setIncidentResponse] = useState<any>();
    const {
        setIsCreateIncidentOpen,
        setTicketRespLoading,
        setConfirmTicketCreation
    } = setters;
    const {
        ticketRespLoading,
        confirmTicketCreation
    } = getters;

    const createIncidentApi = async (message: string) => {
        setTicketRespLoading(true);
        setIsEditIncident(false);
        try {
            const incidentResponse = await raiseIncident(message, contactNumber, incidentFiles);
            setIncidentResponse(incidentResponse);
            setConfirmTicketCreation(true);
        } catch (popupError) {
            console.error(
                "Something went wrong. Please try again later.",
                popupError
            );
        } finally {
            setTicketRespLoading(false);
            setIncidentFiles([]);
        }
    };

    return (
        <div className="bot-header">
            <div className="eon-logo">
                <ThemedIcon name={"AiIcon"} />
            </div>
            <div className="bot-reply-container">
                <span className="bot-heading">
                    {t("chatBot.creatingIncident")}
                </span>
                <div className="ticket-container">
                    <div className="ticket-description">
                        <span className="">
                            {t("chatBot.ticketDescription")}
                        </span>
                    </div>
                    <div className="message-content">
                        <CreateIncidentInput isEditIncident={isEditIncident} />
                    </div>
                    {!confirmTicketCreation && (
                        <div className="buttons-section">
                            <EonUiTagPillGroup>
                                <EonUiTagPill
                                    text={
                                        isEditIncident
                                            ? t("chatBot.createIncident")
                                            : t("chatBot.confirmCreateIncident")
                                    }
                                    class="feedback-tag"
                                    scheme="neutral800"
                                    onClick={() =>
                                        createIncidentApi(incidentText)
                                    }
                                ></EonUiTagPill>
                                {!isEditIncident && (
                                    <EonUiTagPill
                                        text={t("chatBot.editCreateIncident")}
                                        onClick={() => setIsEditIncident(true)}
                                        scheme="neutral800"
                                    ></EonUiTagPill>
                                )}
                                <EonUiTagPill
                                    text={t("cancel")}
                                    scheme="neutral800"
                                    onClick={() =>
                                        setIsCreateIncidentOpen(false)
                                    }
                                ></EonUiTagPill>
                            </EonUiTagPillGroup>
                        </div>
                    )}
                    <CreateIncidentResponse
                        incidentResponse={incidentResponse}
                        confirmTicketCreation={confirmTicketCreation}
                        respLoading={ticketRespLoading}
                    />
                </div>
            </div>
        </div>
    );
};

export default FeedbackCreateIncidentComponent;
